/**
 * Region Detector - Alpine.js Component
 * 
 * Handles client-side geolocation detection, URL comparison,
 * and region suggestion UI management.
 * 
 * Dependencies:
 * - Alpine.js (included via WordPress/theme)
 * - js-cookie (for cookie management)
 * 
 * Data Flow:
 * 1. PHP reads Cloudflare CF-IPCountry header
 * 2. wp_localize_script passes data to GlobberRegion object
 * 3. This script compares user location vs current site
 * 4. Shows appropriate UI (popup or nothing)
 * 5. Pushes events to dataLayer for GTM tracking
 * 
 * @package Globber
 * @since 1.0.0
 */

(function() {
    'use strict';

    /**
     * Debug logger - only outputs when WP_DEBUG is true
     * @param {...any} args - Arguments to log
     */
    const log = (typeof GlobberRegion !== 'undefined' && GlobberRegion.debug)
        ? (...args) => console.log('[Globber Region]', ...args)
        : () => {};

    /**
     * Debug warning logger - only outputs when WP_DEBUG is true
     * @param {...any} args - Arguments to log
     */
    const warn = (typeof GlobberRegion !== 'undefined' && GlobberRegion.debug)
        ? (...args) => console.warn('[Globber Region]', ...args)
        : () => {};

    // Exit early if GlobberRegion data is not available
    if (typeof GlobberRegion === 'undefined') {
        warn('GlobberRegion data not found. Skipping region detection.');
        return;
    }

    // Exit if required dependencies are missing
    if (typeof Cookies === 'undefined') {
        warn('js-cookie library not found.');
        return;
    }

    /**
     * Cookie name for storing user's preferred region
     */
    const COOKIE_NAME = GlobberRegion.cookieName || 'globber_preferred_region';

    /**
     * Cookie expiry in days
     */
    const COOKIE_EXPIRY = GlobberRegion.popup?.cookieExpiry || 30;

    /**
     * Push event to dataLayer for GTM tracking
     * 
     * @param {string} eventName - The event name (e.g., 'region_popup_shown')
     * @param {Object} eventData - Additional data to include in the event
     */
    function pushToDataLayer(eventName, eventData = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            region_detection: {
                current_site_code: GlobberRegion.currentSiteCode || '',
                current_site_name: GlobberRegion.currentSiteName || 'Global',
                user_country: GlobberRegion.userCountry || '',
                ...eventData
            }
        });
        log('DataLayer push:', eventName, eventData);
    }

    /**
     * Check if user has already made a region preference
     * 
     * @returns {string|null} The stored region code or null
     */
    function getStoredPreference() {
        return Cookies.get(COOKIE_NAME) || null;
    }

    /**
     * Store user's region preference in a cookie
     * 
     * @param {string} isoCode - The 2-letter ISO country code
     */
    function setStoredPreference(isoCode) {
        Cookies.set(COOKIE_NAME, isoCode.toUpperCase(), { 
            expires: COOKIE_EXPIRY,
            sameSite: 'Lax',
            secure: window.location.protocol === 'https:'
        });
    }

    /**
     * Session storage key for tracking popup dismissal during current visit
     */
    const SESSION_DISMISSED_KEY = 'globber_region_popup_dismissed';

    /**
     * Check if popup was dismissed during this session
     * 
     * @returns {boolean} True if popup was dismissed this session
     */
    function wasPopupDismissedThisSession() {
        try {
            return sessionStorage.getItem(SESSION_DISMISSED_KEY) === 'true';
        } catch (e) {
            return false;
        }
    }

    /**
     * Mark popup as dismissed for this session
     */
    function setPopupDismissedThisSession() {
        try {
            sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
        } catch (e) {
            // sessionStorage not available, ignore
        }
    }

    /**
     * Find country data by ISO code from the country map
     * Returns an array of all matching entries (supports multiple languages per country)
     * 
     * @param {string} isoCode - The 2-letter ISO country code
     * @returns {Array|null} Array of country data objects or null
     */
    function findCountriesByCode(isoCode) {
        if (!isoCode || !GlobberRegion.countryMap) {
            return null;
        }
        
        const code = isoCode.toUpperCase();
        const entries = GlobberRegion.countryMap[code];
        
        // Return null if no entries, otherwise return the array
        return (entries && entries.length > 0) ? entries : null;
    }

    /**
     * Check if user is on the correct regional site
     * Compares the user's detected country code with the current site's ISO code
     * 
     * Special case: If the current site has no ISO code (catalogue/global site),
     * and the user has a detected country that exists in our country map,
     * they should be shown the popup to go to their regional site.
     * 
     * @param {string} userCountryCode - The user's detected country ISO code
     * @returns {boolean} True if user is on the correct site
     */
    function isOnCorrectSite(userCountryCode) {
        if (!userCountryCode) {
            return true; // If no country detected, assume correct
        }

        const currentSiteCode = (GlobberRegion.currentSiteCode || '').toUpperCase();
        const userCode = userCountryCode.toUpperCase();
        
        // If no current site code configured (catalogue/global site),
        // check if user's country exists in our country map
        // If it does, they should be redirected to their regional site
        if (!currentSiteCode) {
            const countryEntries = findCountriesByCode(userCode);
            // Return false (not on correct site) if we have a regional site for them
            return !countryEntries;
        }
        
        return currentSiteCode === userCode;
    }

    /**
     * Replace placeholders in text strings
     * 
     * @param {string} text - Text with placeholders like {country}, {language}
     * @param {Object} data - Data object with replacement values
     * @returns {string} Text with placeholders replaced
     */
    function replacePlaceholders(text, data) {
        if (!text) return '';
        
        return text
            .replace(/{country}/gi, data.name || '')
            .replace(/{language}/gi, data.language || '');
    }

    /**
     * Alpine.js component for region detection logic
     * 
     * This component manages the state and behavior of the region
     * suggestion popup and manual region picker modal.
     */
    function regionDetector() {
        return {
            // State
            isPopupVisible: false,
            isSelectionModalVisible: false,
            isReminderVisible: false,
            isInitialized: false,
            
            // User's detected country data (array - may have multiple languages)
            detectedCountries: [],
            detectedCountryCode: '',
            
            // Preferred region data (from cookie)
            preferredCountry: null,
            preferredUrl: '',
            
            // Current site info
            currentSiteCode: GlobberRegion.currentSiteCode || '',
            currentSiteName: GlobberRegion.currentSiteName || 'Global',
            
            // Processed text strings
            popupTitle: '',
            popupMessage: '',
            goButtonText: '',
            stayButtonText: '',
            
            // Regions for modal picker (ensure it's always an array)
            regions: Array.isArray(GlobberRegion.regions) ? GlobberRegion.regions : [],
            
            // Search filter for modal
            searchQuery: '',
            
            /**
             * Initialize the component
             */
            init() {
                // Prevent double initialization
                if (this.isInitialized) return;
                this.isInitialized = true;
                
                log('Component initialized');
                log('GlobberRegion data:', GlobberRegion);

                // Run detection logic
                this.detectAndSuggest();

                // Listen for manual picker trigger clicks (e.g., "All regions..." links)
                document.addEventListener('click', (e) => {
                    const trigger = e.target.closest('[data-region-picker-trigger]');
                    if (trigger) {
                        e.preventDefault();
                        this.openSelectionModal();
                    }
                });
            },

            /**
             * Main detection logic
             * 
             * Step 1: Check for existing cookie preference - show reminder if on different site
             * Step 2: Fetch user's location via AJAX (bypasses page cache)
             * Step 3: Find their target URL
             * Step 4: Compare with current site
             * Step 5: Show popup if needed
             */
            async detectAndSuggest() {
                log('Running detectAndSuggest...');
                
                // Check if popup was already dismissed this session
                if (wasPopupDismissedThisSession()) {
                    log('Popup was dismissed this session, not showing again.');
                    return;
                }
                
                // Step 1: Check for stored preference
                const storedPreference = getStoredPreference();
                if (storedPreference) {
                    log('Stored preference found:', storedPreference);
                    
                    // Check if user is on their preferred site
                    if (!isOnCorrectSite(storedPreference)) {
                        // User has a preference but is browsing a different site
                        // Show a small reminder banner (non-intrusive)
                        const preferredEntries = findCountriesByCode(storedPreference);
                        if (preferredEntries && preferredEntries.length > 0) {
                            // Use first entry for reminder banner
                            const preferredData = preferredEntries[0];
                            this.preferredCountry = Object.assign({}, preferredData, {
                                isoCode: storedPreference
                            });
                            this.preferredUrl = preferredData.url;
                            
                            // Show reminder after a short delay
                            setTimeout(() => {
                                this.isReminderVisible = true;
                                log('Showing reminder banner for preferred site:', storedPreference);
                                
                                // Track reminder banner shown
                                pushToDataLayer('region_reminder_shown', {
                                    preferred_country_code: storedPreference,
                                    preferred_country_name: preferredData.name,
                                    preferred_url: preferredData.url
                                });
                            }, 1000);
                        }
                    }
                    // Don't show the main popup since they already made a choice
                    return;
                }

                // Step 2: Fetch user's country via AJAX (bypasses page cache)
                let userCountryCode = null;
                try {
                    const formData = new FormData();
                    formData.append('action', 'globber_get_user_country');
                    
                    const response = await fetch(GlobberRegion.ajaxUrl, {
                        method: 'POST',
                        body: formData,
                        credentials: 'same-origin'
                    });
                    
                    const result = await response.json();
                    if (result.success && result.data.countryCode) {
                        userCountryCode = result.data.countryCode;
                    }
                } catch (error) {
                    log('Error fetching user country:', error);
                    return;
                }
                
                log('User country code:', userCountryCode);
                
                if (!userCountryCode) {
                    // No country detected (no Cloudflare header), skip
                    log('No country detected from Cloudflare.');
                    return;
                }

                // Step 3: Find target URL for user's country
                const countryEntries = findCountriesByCode(userCountryCode);
                log('Country entries:', countryEntries);
                
                if (!countryEntries || countryEntries.length === 0) {
                    // Country not in our region list, user stays on current site
                    log('Country not in region list.');
                    return;
                }

                // Step 4: Compare user's country with current site's country code
                const onCorrectSite = isOnCorrectSite(userCountryCode);
                log('Is on correct site:', onCorrectSite, 'Current site code:', GlobberRegion.currentSiteCode, 'User country:', userCountryCode);
                
                if (onCorrectSite) {
                    // User is already on the correct site
                    log('User already on correct site.');
                    return;
                }

                // Step 5: User should be redirected - show the popup
                log('Showing popup - user should be redirected');
                
                // Store all country entries (with ISO code added)
                this.detectedCountries = countryEntries.map(entry => 
                    Object.assign({}, entry, { isoCode: userCountryCode })
                );
                this.detectedCountryCode = userCountryCode;
                
                // Process popup text with first country's data for the header
                const popupSettings = GlobberRegion.popup || {};
                const firstCountry = countryEntries[0];
                this.popupTitle = replacePlaceholders(popupSettings.title, firstCountry);
                this.popupMessage = replacePlaceholders(popupSettings.message, firstCountry);
                this.goButtonText = replacePlaceholders(popupSettings.goButton, firstCountry);
                this.stayButtonText = popupSettings.stayButton || 'Stay on current site';
                
                // Show the popup (with slight delay for better UX)
                setTimeout(() => {
                    this.isPopupVisible = true;
                    
                    // Track popup shown
                    pushToDataLayer('region_popup_shown', {
                        detected_country_code: userCountryCode,
                        detected_country_name: this.detectedCountries[0]?.name || '',
                        suggested_urls: this.detectedCountries.map(c => c.url),
                        language_options: this.detectedCountries.length
                    });
                }, 500);
            },

            /**
             * Handle "Go to [Country] site" button click
             * @param {Object} country - The country entry to redirect to
             */
            goToSuggestedSite(country) {
                if (!country || !country.url) return;
                
                // Track user accepting suggestion
                pushToDataLayer('region_popup_accepted', {
                    action: 'go_to_suggested',
                    selected_country_code: country.isoCode || this.detectedCountryCode,
                    selected_country_name: country.name,
                    selected_language: country.language,
                    destination_url: country.url
                });
                
                // Set cookie to remember preference
                setStoredPreference(country.isoCode || this.detectedCountryCode);
                
                // Close popup
                this.isPopupVisible = false;
                
                // Redirect to target site
                window.location.href = country.url;
            },

            /**
             * Handle "Stay here" button click
             */
            stayOnCurrentSite() {
                // Track user declining suggestion
                pushToDataLayer('region_popup_declined', {
                    action: 'stay_on_current',
                    declined_country_code: this.detectedCountryCode || '',
                    stayed_on_site_code: this.currentSiteCode
                });
                
                // Mark as dismissed for this session (won't show again this visit)
                setPopupDismissedThisSession();
                
                // Close popup
                this.isPopupVisible = false;
            },

            /**
             * Close the popup (same as stay, but no cookie if just closing)
             */
            closePopup() {
                // Track popup dismissed
                pushToDataLayer('region_popup_dismissed', {
                    action: 'close_popup',
                    dismissed_country_code: this.detectedCountryCode || '',
                    stayed_on_site_code: this.currentSiteCode
                });
                
                // Mark as dismissed for this session (won't show again this visit)
                setPopupDismissedThisSession();
                this.isPopupVisible = false;
            },

            /**
             * Dismiss the reminder banner
             * Does NOT change the stored preference
             */
            dismissReminder() {
                // Track reminder dismissed
                pushToDataLayer('region_reminder_dismissed', {
                    action: 'dismiss_reminder',
                    preferred_country_code: this.preferredCountry?.isoCode || '',
                    preferred_country_name: this.preferredCountry?.name || ''
                });
                
                this.isReminderVisible = false;
            },

            /**
             * Go to preferred site from reminder banner
             */
            goToPreferredSite() {
                if (!this.preferredUrl) return;
                
                // Track reminder accepted
                pushToDataLayer('region_reminder_accepted', {
                    action: 'go_to_preferred',
                    preferred_country_code: this.preferredCountry?.isoCode || '',
                    preferred_country_name: this.preferredCountry?.name || '',
                    destination_url: this.preferredUrl
                });
                
                this.isReminderVisible = false;
                window.location.href = this.preferredUrl;
            },

            /**
             * Open the country selection modal (full list)
             */
            openSelectionModal() {
                // Close popup and reminder if open
                this.isPopupVisible = false;
                this.isReminderVisible = false;
                
                // Track modal opened
                pushToDataLayer('region_selector_opened', {
                    action: 'open_country_selector',
                    trigger_source: this.isPopupVisible ? 'popup_link' : 'header_link'
                });
                
                // Disable body scroll
                document.body.style.overflow = 'hidden';
                
                // Show selection modal
                this.isSelectionModalVisible = true;
            },

            /**
             * Close the country selection modal
             */
            closeSelectionModal() {
                // Track modal closed (without selection)
                pushToDataLayer('region_selector_closed', {
                    action: 'close_country_selector'
                });
                
                // Re-enable body scroll
                document.body.style.overflow = '';
                
                // Hide selection modal
                this.isSelectionModalVisible = false;
            },

            /**
             * Handle region selection from the modal
             * 
             * @param {string} isoCode - The selected country's ISO code
             * @param {string} url - The target URL
             * @param {string} countryName - The country name for tracking
             */
            selectRegion(isoCode, url, countryName) {
                if (!isoCode || !url) return;
                
                // Check if we're already on this site (compare ISO codes)
                const alreadyOnSite = isOnCorrectSite(isoCode);
                
                // Track country selection
                pushToDataLayer('region_country_selected', {
                    action: 'select_country',
                    selected_country_code: isoCode,
                    selected_country_name: countryName || '',
                    destination_url: url,
                    will_redirect: !alreadyOnSite
                });
                
                // Set cookie
                setStoredPreference(isoCode);
                
                // Close modal (without triggering the close event again)
                document.body.style.overflow = '';
                this.isSelectionModalVisible = false;
                
                if (alreadyOnSite) {
                    // Already on correct site, just close
                    return;
                }
                
                // Redirect
                window.location.href = url;
            },

            /**
             * Get filtered regions based on search query
             * 
             * @returns {Array} Filtered regions array
             */
            get filteredRegions() {
                // Ensure regions is an array
                if (!Array.isArray(this.regions)) {
                    return [];
                }
                
                if (!this.searchQuery || !this.searchQuery.trim()) {
                    return this.regions;
                }
                
                const query = this.searchQuery.toLowerCase().trim();
                
                return this.regions.map(region => {
                    if (!region || !Array.isArray(region.countries)) {
                        return null;
                    }
                    
                    const filteredCountries = region.countries.filter(country => 
                        country && (
                            (country.name && country.name.toLowerCase().includes(query)) ||
                            (country.isoCode && country.isoCode.toLowerCase().includes(query)) ||
                            (country.language && country.language.toLowerCase().includes(query))
                        )
                    );
                    
                    if (filteredCountries.length === 0) {
                        return null;
                    }
                    
                    return Object.assign({}, region, {
                        countries: filteredCountries
                    });
                }).filter(Boolean);
            },

            /**
             * Check if manual picker link should be shown
             */
            get showPickerLink() {
                return GlobberRegion.popup?.showPicker ?? true;
            },

            /**
             * Get picker link text
             */
            get pickerLinkText() {
                return GlobberRegion.popup?.pickerText || 'Choose a different location';
            }
        };
    }

    // Register the Alpine.js component
    document.addEventListener('alpine:init', () => {
        Alpine.data('regionDetector', regionDetector);
    });

    // Fallback: If Alpine is already loaded, register immediately
    if (typeof Alpine !== 'undefined') {
        Alpine.data('regionDetector', regionDetector);
    }

})();
